import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../store/auth";
import { useMeta } from "../hooks";
import { useToast } from "../store/toast";
import { Modal, StatusBadge, TaskStatusBadge, PriorityBadge, Avatar, RiskBadge, MicButton, VoiceNoteButton, TabButton, TabTrack } from "./ui";
import Icon from "./Icon";
import FloatingPopover from "./FloatingPopover";
import AttachmentPreview, { isPreviewable } from "./AttachmentPreview";
import FollowButton from "./FollowButton";
import ConvertirEnRequisicion from "./ConvertirEnRequisicion";
import ConstanciaModal from "./ConstanciaModal";
import ConstanciaHistorial from "./ConstanciaHistorial";
import { CustomFieldsView, useCustomSchema } from "./CustomFieldsForm";
import RichText from "./RichText";
import { STATUS, SUPPLY, SUPPLY_ORDER, isSupplyStage } from "../constants";

// Tiempo en HORAS + MINUTOS (humano). Internamente sigue siendo horas decimales
// (compatibilidad con el modelo), pero se entra y se ve como "1h 30m".
export function fmtHm(horasDecimal) {
  const t = Math.max(0, Number(horasDecimal) || 0);
  const h = Math.floor(t);
  const m = Math.round((t - h) * 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}
export function HorasMin({ horas, onSave, disabled }) {
  const total = Number(horas) || 0;
  const [h, setH] = useState(Math.floor(total));
  const [m, setM] = useState(Math.round((total - Math.floor(total)) * 60));
  useEffect(() => {
    const t = Number(horas) || 0;
    setH(Math.floor(t));
    setM(Math.round((t - Math.floor(t)) * 60));
  }, [horas]);
  const commit = (nh, nm) => {
    nh = Math.max(0, Math.floor(Number(nh) || 0));
    nm = Math.min(59, Math.max(0, Math.floor(Number(nm) || 0)));
    const dec = Math.round((nh + nm / 60) * 100) / 100;
    if (dec !== total) onSave?.(dec);
  };
  return (
    <div className="flex items-center gap-1">
      <input type="number" min="0" disabled={disabled} value={h}
        onChange={(e) => setH(e.target.value)} onBlur={() => commit(h, m)}
        className="input h-8 w-14 py-1 text-right" aria-label="horas" />
      <span className="text-xs text-slate-400">h</span>
      <input type="number" min="0" max="59" disabled={disabled} value={m}
        onChange={(e) => setM(e.target.value)} onBlur={() => commit(h, m)}
        className="input h-8 w-14 py-1 text-right" aria-label="minutos" />
      <span className="text-xs text-slate-400">m</span>
    </div>
  );
}
import { fmtDate, fmtDateTime, fmtStamp, fmtStampDate, todayLocal, comprimirImagenSiAplica } from "../utils";

// Highlight @mentions inside a comment body.
// Muestra una imagen adjunta INLINE en el chat (carga el blob con auth). Clic
// abre/descarga en grande.
function ChatImage({ taskId, attId, name, onOpen }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let alive = true, made = "";
    api.get(`/tasks/${taskId}/attachments/${attId}/download`, { responseType: "blob" })
      .then((r) => { if (alive) { made = URL.createObjectURL(r.data); setUrl(made); } })
      .catch(() => {});
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [taskId, attId]);
  if (!url) return <span className="my-0.5 inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-[11px] text-slate-400">cargando imagen…</span>;
  return (
    <button type="button" onClick={onOpen} className="my-0.5 block" title={name}>
      <img src={url} alt={name} className="max-h-52 max-w-[240px] rounded-lg border border-slate-200 object-contain hover:opacity-90" />
    </button>
  );
}

function renderBody(text) {
  return text.split(/(@[a-zA-Z0-9._-]{2,40})/g).map((part, i) =>
    part.startsWith("@")
      ? <span key={i} className="font-semibold text-brand-700">{part}</span>
      : <span key={i}>{part}</span>
  );
}

function handleOf(u) {
  return (u.email || "").split("@")[0] || u.full_name.toLowerCase().replace(/\s+/g, ".");
}
// Alias de equipo (@arquitectura) desde el nombre del departamento: primera
// palabra significativa, en minúscula sin tildes.
function teamHandle(name = "") {
  const norm = name.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const w = norm.split(/\s+/).filter((x) => !["de", "y", "e", "la", "el", "del", "los", "las"].includes(x));
  return (w[0] || norm.replace(/\s+/g, "")).trim();
}

function fileIcon(ct = "", name = "") {
  const n = name.toLowerCase();
  if (n.match(/\.(xls|xlsx|csv)$/)) return "sheet";
  return "fileText";
}

function fmtDuration(ms) {
  if (!ms || ms < 0) ms = 0;
  const mins = Math.floor(ms / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${mins % 60}m`;
  return `${mins}m`;
}

// Reconstructs how long a task spent in each status from its activity log
// (every status change is already recorded there with a timestamp).
function computeStatusTimeline(task) {
  const changes = (task.activity || [])
    .filter((a) => a.action === "status_changed")
    .slice()
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const start = task.created_at ? new Date(task.created_at) : (changes[0] ? new Date(changes[0].created_at) : new Date());
  let curStatus = changes.length > 0 ? changes[0].old_value : task.status;
  let curStart = start;
  const segments = [];
  changes.forEach((c) => {
    const end = new Date(c.created_at);
    segments.push({ status: curStatus, from: curStart, to: end });
    curStatus = c.new_value;
    curStart = end;
  });
  segments.push({ status: curStatus, from: curStart, to: new Date(), ongoing: task.status !== "completed" });
  return segments;
}

export default function TaskDetailPanel({ taskId, open, onClose, onChanged, onEdit }) {
  const { user, roleAtLeast, readonly, features, can } = useAuth();
  const { users, departments } = useMeta();
  const { push } = useToast();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [despachos, setDespachos] = useState(null);
  const [comment, setComment] = useState("");
  const [pendingFile, setPendingFile] = useState(null);   // archivo por enviar (estilo WhatsApp)
  const [cEditId, setCEditId] = useState(null);
  const [cEditText, setCEditText] = useState("");
  const [tab, setTab] = useState("info");
  useEffect(() => {
    // Se carga apenas la tarea abre (no solo al entrar al tab Despachos):
    // el estado del envío se muestra junto al título — pedido 2026-08-11.
    // OJO: no filtrar por task.task_type — tareas "Envío de equipos" viejas
    // quedaron con task_type="inventario" (bug corregido para nuevas, pero
    // no retroactivo en la tarea misma) y no mostrarían el chip. El propio
    // resultado de /seguimiento (vacío o no) decide si hay algo que mostrar.
    if (can?.("compras_gestion") && task?.id && despachos === null) {
      api.get("/packing/seguimiento", { params: { task_id: task.id } }).then((r) => setDespachos(r.data)).catch(() => setDespachos([]));
    }
  }, [task?.id, can]);
  const [sharing, setSharing] = useState(false);
  const [constanciaOpen, setConstanciaOpen] = useState(false);
  const [constanciaRefresh, setConstanciaRefresh] = useState(0);
  const [allTasks, setAllTasks] = useState([]);
  const [depQuery, setDepQuery] = useState("");
  const depAnchorRef = useRef(null);
  const [progress, setProgress] = useState(null);   // null = not loaded yet
  const [newEntry, setNewEntry] = useState({ description: "", hours: "", work_date: todayLocal(), participant_id: "" });
  const [savingEntry, setSavingEntry] = useState(false);
  const [editEntryId, setEditEntryId] = useState(null);
  const fileRef = useRef(null);
  const progressFileRef = useRef(null);
  const chatFileRef = useRef(null);
  const [uploadingForProgress, setUploadingForProgress] = useState(null);
  const [previewAtt, setPreviewAtt] = useState(null);  // adjunto en preview modal
  const chatEnd = useRef(null);
  // C2 — pestañas personalizadas aplicables a esta tarea (por tipo + equipo).
  // Solo las que tienen al menos un campo aparecen en la ficha.
  const { applicableTabs, tabFields } = useCustomSchema("task", task?.department_id, task?.task_type);
  const customTabs = applicableTabs.filter((t) => tabFields(t.id).length > 0);

  // Si el GET falla, avisamos en vez de quedarnos con la tarea previa cargada
  // (eso hacía que "abriera otra"). No reseteamos aquí para no parpadear en los
  // refrescos tras comentar/cambiar estado.
  const load = () => {
    if (!taskId) return;
    api.get(`/tasks/${taskId}`)
      .then((r) => setTask(r.data))
      .catch((e) => push(e.response?.data?.detail || "No se pudo abrir la tarea", "error"));
  };
  useEffect(() => {
    if (!open) return;
    // Pestaña inicial solicitada por quien abrió el panel (ej. clic en el
    // cuadro "Avances" del Kanban abre directo el Registro de Avances).
    const wanted = sessionStorage.getItem("tf:open-tab");
    sessionStorage.removeItem("tf:open-tab");
    setTab(wanted || "info");
    setTask(null);   // limpiar la tarea anterior mientras carga / si falla la nueva
    setDespachos(null);
    load();
    setProgress(null);
  }, [taskId, open]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadProgress = () => { if (taskId) api.get(`/tasks/${taskId}/progress`).then((r) => setProgress(r.data)).catch(() => setProgress([])); };
  useEffect(() => { if (tab === "bitacora" && progress === null) loadProgress(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open && allTasks.length === 0) api.get("/tasks").then((r) => setAllTasks(r.data)).catch(() => {}); }, [open]);

  const addDep = async (depId) => { await api.post(`/tasks/${task.id}/dependencies`, { depends_on_id: Number(depId) }); setDepQuery(""); load(); onChanged?.(); };
  const removeDep = async (depRowId) => { await api.delete(`/tasks/${task.id}/dependencies/${depRowId}`); load(); onChanged?.(); };
  useEffect(() => { if (tab === "chat") chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [task, tab]);

  // Equipos mencionables (@arquitectura). Debe declararse ANTES de cualquier
  // return condicional para no romper el orden de hooks (React #310).
  const teams = useMemo(() => {
    const seen = new Set();
    return (departments || []).map((d) => ({ team: true, id: `t${d.id}`, name: d.name, handle: teamHandle(d.name) }))
      .filter((t) => t.handle && !seen.has(t.handle) && seen.add(t.handle));
  }, [departments]);

  if (!task) return null;

  const changeStatus = async (status) => {
    if (task.federado_readonly) return;   // espejo de Panamá → solo lectura (Fase 1)
    await api.patch(`/tasks/${task.id}`, { status }); load(); onChanged?.();
    push(`Estado → ${STATUS[status].label}`, "success");
  };
  const changeSupply = async (key) => {
    if (task.federado_readonly) return;   // espejo de Panamá → solo lectura (Fase 1)
    const s = SUPPLY[key];
    await api.patch(`/tasks/${task.id}`, { stage: key, status: s.base }); load(); onChanged?.();
    push(`Suministro → ${s.label}`, "success");
  };
  const addComment = async (e) => {
    e.preventDefault();
    const text = comment.trim();
    if (!text && !pendingFile) return;
    try {
      let body = text;
      if (pendingFile) {
        const archivo = await comprimirImagenSiAplica(pendingFile);
        const fd = new FormData(); fd.append("file", archivo);
        const { data: att } = await api.post(`/tasks/${task.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        body = `${text ? text + " " : ""}[[file:${att.id}:${att.filename}]]`;
      }
      await api.post(`/tasks/${task.id}/comments`, { body });
      setComment(""); setPendingFile(null); load(); onChanged?.();
    } catch { push("No se pudo enviar el mensaje", "error"); }
  };
  const saveEditComment = async (id) => {
    const body = cEditText.trim();
    if (!body) return;
    try { await api.patch(`/tasks/${task.id}/comments/${id}`, { body }); setCEditId(null); load(); }
    catch (e) { push(e.response?.data?.detail || "No se pudo editar", "error"); }
  };
  const deleteComment = async (id) => {
    if (!window.confirm("¿Borrar este mensaje?")) return;
    try { await api.delete(`/tasks/${task.id}/comments/${id}`); load(); }
    catch (e) { push(e.response?.data?.detail || "No se pudo borrar", "error"); }
  };
  const toggleItem = async (i) => {
    // Country Manager: no tiene edición general de la tarea, solo esta
    // excepción puntual (FASE 9, checklist compartido) — usa el endpoint
    // acotado en vez del PATCH completo de la tarea.
    if (user?.role === "country_manager") {
      await api.patch(`/tasks/${task.id}/checklist-item/marcar`, { index: i, done: !task.items[i]?.done });
    } else {
      const items = (task.items || []).map((it, idx) => (idx === i ? { ...it, done: !it.done } : it));
      await api.patch(`/tasks/${task.id}`, { items });
    }
    load(); onChanged?.();
  };
  // Asignar el responsable de un punto del checklist: "Contratista" (externo,
  // sin cuenta en el sistema) o un integrante real del equipo del
  // departamento — a diferencia del texto libre de la plantilla ("IT",
  // "IT / Contratista"...), esto es accionable: queda un user_id real.
  const setItemResponsable = async (i, value) => {
    const esContratista = value === "contratista";
    const u = esContratista ? null : (users || []).find((x) => String(x.id) === value);
    const items = (task.items || []).map((it, idx) => idx === i ? {
      ...it,
      responsable_user_id: esContratista ? null : (u ? u.id : null),
      responsable_tipo: esContratista ? "contratista" : (u ? "usuario" : ""),
      responsable: esContratista ? "Contratista" : (u ? u.full_name : ""),
    } : it);
    await api.patch(`/tasks/${task.id}`, { items }); load(); onChanged?.();
  };
  // Mover un ítem dentro de SU MISMO día (arriba/abajo) — pedido 2026-08-25.
  // `posEnGrupo` es la posición visible dentro del grupo del día, no el
  // índice real del arreglo (los ítems de días distintos pueden intercalarse).
  const moverItem = async (dia, posEnGrupo, dir) => {
    const filas = (task.items || []).map((it, i) => ({ it, i })).filter(({ it }) => (it.dia || "—") === dia);
    const otraPos = posEnGrupo + dir;
    if (otraPos < 0 || otraPos >= filas.length) return;
    const idxA = filas[posEnGrupo].i, idxB = filas[otraPos].i;
    const items = [...task.items];
    [items[idxA], items[idxB]] = [items[idxB], items[idxA]];
    try {
      await api.patch(`/tasks/${task.id}`, { items });
      load(); onChanged?.();
    } catch (e) { push(e.response?.data?.detail || "No se pudo mover el ítem", "error"); }
  };
  // Segundo check ("Verificado") — SOLO auditor/responsable/supervisor (el
  // backend lo exige de nuevo; `task.puede_auditar_checklist` solo decide si
  // se muestra habilitado). Pedido 2026-08-25.
  const toggleVerificado = async (i) => {
    try {
      await api.patch(`/tasks/${task.id}/checklist-item/verificar`,
        { index: i, verificado: !task.items[i]?.verificado });
      load(); onChanged?.();
    } catch (e) { push(e.response?.data?.detail || "No se pudo verificar", "error"); }
  };

  const addProgressEntry = async () => {
    if (!newEntry.description.trim()) return push("Describe el avance", "warning");
    setSavingEntry(true);
    try {
      const payload = {
        description: newEntry.description.trim(),
        hours: Number(newEntry.hours) || 0,
        work_date: newEntry.work_date || null,
        participant_id: newEntry.participant_id ? Number(newEntry.participant_id) : null,
      };
      if (editEntryId) {
        await api.patch(`/tasks/${task.id}/progress/${editEntryId}`, payload);
        push("Avance actualizado", "success");
      } else {
        await api.post(`/tasks/${task.id}/progress`, payload);
        push("Avance del día registrado", "success");
      }
      setNewEntry({ description: "", hours: "", work_date: todayLocal(), participant_id: "" });
      setEditEntryId(null);
      loadProgress(); load(); onChanged?.();
    } catch (err) { push(err.response?.data?.detail || "No se pudo guardar", "error"); }
    finally { setSavingEntry(false); }
  };
  const startEditEntry = (p) => {
    setEditEntryId(p.id);
    setNewEntry({
      description: p.description || "", hours: p.hours ? String(p.hours) : "",
      work_date: p.work_date || todayLocal(), participant_id: p.participant?.id ? String(p.participant.id) : "",
    });
  };
  const cancelEditEntry = () => {
    setEditEntryId(null);
    setNewEntry({ description: "", hours: "", work_date: todayLocal(), participant_id: "" });
  };
  const deleteProgressEntry = async (id) => {
    if (!window.confirm("¿Eliminar este avance?")) return;
    try {
      await api.delete(`/tasks/${task.id}/progress/${id}`);
      if (editEntryId === id) cancelEditEntry();
      loadProgress(); load(); onChanged?.();
    }
    catch { push("No se pudo eliminar", "error"); }
  };

  const uploadFile = async (e, progressId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const archivo = await comprimirImagenSiAplica(file);
    const fd = new FormData(); fd.append("file", archivo);
    const url = progressId
      ? `/tasks/${task.id}/attachments?progress_id=${progressId}`
      : `/tasks/${task.id}/attachments`;
    try {
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      push("Adjunto subido", "success"); load(); if (progressId) loadProgress();
    } catch { push("Error al subir el archivo", "error"); }
    e.target.value = "";
  };
  const download = async (att) => {
    const res = await api.get(`/tasks/${task.id}/attachments/${att.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = att.filename; a.click();
    URL.revokeObjectURL(url);
  };
  // Adjuntar un archivo DESDE el chat: se sube como anexo de la tarea y el
  // mensaje lo referencia (aparece en Adjuntos y en Archivos del proyecto).
  // WhatsApp-style: al elegir el archivo NO se envía; queda pendiente para que
  // puedas escribir un mensaje y/o @mencionar y enviarlo TODO junto.
  const uploadChatFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) setPendingFile(file);
  };
  const isImageName = (name) => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name || "");
  const delAtt = async (att) => { await api.delete(`/tasks/${task.id}/attachments/${att.id}`); load(); };

  // Renderiza un mensaje de chat: texto (con @menciones) + fichas de archivo
  // [[file:id:nombre]] como enlace clicable que abre/descarga el anexo.
  const renderMessage = (body) => {
    const parts = String(body || "").split(/(\[\[file:\d+:[^\]]+\]\])/g);
    return parts.map((p, i) => {
      const m = p.match(/^\[\[file:(\d+):(.+)\]\]$/);
      if (!m) return <span key={i}>{renderBody(p)}</span>;
      const [, id, name] = m;
      const att = { id: Number(id), filename: name };
      if (isImageName(name)) {
        return <ChatImage key={i} taskId={task.id} attId={Number(id)} name={name} onOpen={() => download(att)} />;
      }
      return (
        <button key={i} type="button" onClick={() => download(att)}
          className="my-0.5 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1 text-left text-xs text-slate-700 hover:border-brand-300">
          <Icon name={fileIcon("", name)} size={13} className="text-slate-400" />
          <span className="max-w-[180px] truncate">{name}</span>
          <Icon name="download" size={11} className="text-slate-300" />
        </button>
      );
    });
  };

  const duplicate = async () => {
    const name = prompt("Nombre de la copia (se duplican las subtareas):", `${task.title} (copia)`);
    if (name === null) return;
    try {
      await api.post(`/tasks/${task.id}/duplicate`, { title: name });
      push("Tarea duplicada con sus subtareas", "success");
      onChanged?.();
      onClose();
    } catch (e) { push(e.response?.data?.detail || "No se pudo duplicar", "error"); }
  };
  const removeTask = async () => {
    if (!window.confirm(`Vas a archivar la tarea «${task.title}».\n\nSe ocultará de los tableros y listados (no se borra de la base de datos; un administrador puede restaurarla).\n\n¿Continuar?`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      push("Tarea archivada", "success");
      onChanged?.();
      onClose();
    } catch (e) { push(e.response?.data?.detail || "No se pudo eliminar", "error"); }
  };

  // Editar exige ser propietario (creador/asignado) o supervisor+; cualquiera
  // que pueda VER la tarea puede registrar avances (espejo de _can_edit backend).
  const bossRoles = ["supervisor", "manager", "admin"];
  const isBoss = bossRoles.includes(user?.role) || bossRoles.includes(user?.secondary_role);
  const isOwner = task.creator_id === user?.id || (task.assignees || []).some((a) => a.id === user?.id);
  // Federación (Fase 1): tarea ESPEJO de Panamá → SOLO LECTURA aquí (editar es
  // Fase 2). El backend rechaza la escritura con 409; la UI la deshabilita.
  const esFederado = !!task.federado_readonly;
  const canWrite = !readonly && !esFederado && (isBoss || isOwner);
  const canProgress = !readonly && !esFederado;
  // Eliminar (definitivo): solo el creador de la tarea o un administrador.
  const canDelete = canWrite && (task.creator_id === user?.id || user?.role === "admin");

  const setCollaborators = async (ids) => {
    await api.patch(`/tasks/${task.id}`, { collaborator_ids: ids });
    load(); onChanged?.(); setSharing(false);
    push("Colaboradores actualizados", "success");
  };

  // @mention autocomplete: personas Y equipos (@arquitectura). El equipo se
  // deduce del nombre único de cada departamento visible.
  const mentionM = comment.match(/@([a-zA-Z0-9._-]*)$/);
  const mentionMatches = mentionM
    ? [
        ...users.filter((u) => {
          const q = mentionM[1].toLowerCase();
          return handleOf(u).toLowerCase().includes(q) || u.full_name.toLowerCase().includes(q);
        }).map((u) => ({ id: u.id, name: u.full_name, avatar_url: u.avatar_url, handle: handleOf(u) })),
        ...teams.filter((t) => t.handle.includes(mentionM[1].toLowerCase()) || t.name.toLowerCase().includes(mentionM[1].toLowerCase())),
      ].slice(0, 6)
    : [];
  const pickMention = (u) => setComment(comment.replace(/@([a-zA-Z0-9._-]*)$/, `@${u.handle} `));

  const canSeeActivity = ["admin", "directivo", "manager"].includes(user?.role);
  // Bitácora de seguimiento disponible en todos los tipos de trabajo
  // (reemplaza a Criterios/Subtareas). Inventario/solicitud usan ítems.
  const supportsBitacora = !["inventario", "solicitud"].includes(task.task_type) || task.progress_days > 0;
  const TABS = [
    ["info", "Información", "info"],
    ...(supportsBitacora ? [["bitacora", `Registro de Avances (${task.progress_days || 0}d)`, "calendar"]] : []),
    ["chat", `Chat (${task.comments?.length || 0})`, "message"],
    ["files", `Adjuntos (${task.attachments?.length || 0})`, "paperclip"],
    ...(canSeeActivity ? [["activity", "Actividad", "clock"]] : []),
    // "Despachos" (pedido 2026-08-11): el contenido caja por caja del envío
    // que originó esta tarea, consultable sin salir a Almacén.
    ...(can?.("compras_gestion") ? [["despachos", "Despachos", "truck"]] : []),
    // C2 — pestañas personalizadas (configuradas en Ajustes → Campos y pestañas).
    ...customTabs.map((t) => [`ct:${t.id}`, t.name, "fileText"]),
  ];

  return (
    <Modal open={open} onClose={onClose} xl title={null}>
      <div>
      {/* Encabezado: neutro — el color del módulo queda solo en los chips */}
      <div className="-mx-5 -mt-5 mb-4 rounded-t-xl border-b border-slate-200 bg-white px-5 pb-3 pt-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TaskStatusBadge task={task} />
            <PriorityBadge priority={task.priority} />
            {task.risk && task.risk !== "low" && <RiskBadge risk={task.risk} />}
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="shrink-0 rounded-full p-1 text-tareas/70 transition-colors hover:bg-tareas/10 hover:text-tareas">
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-bold uppercase leading-snug text-tinta">{task.title}</h2>
          {task.task_type === "checklist_apertura" && (task.items || []).length > 0 && (() => {
            const total = task.items.length, done = task.items.filter((i) => i.done).length;
            return (
              <span className="flex items-center gap-1 rounded-tag bg-tareas/10 px-2 py-0.5 text-[11px] font-semibold text-tareas" title="Checklist">
                <Icon name="checkSquare" size={12} /> {done}/{total}
              </span>
            );
          })()}
          {despachos?.length > 0 && (
            <span className="flex flex-wrap gap-1">
              {[...new Map(despachos.map((d) => [d.estado_label || d.estado, 0])).keys()].map((label) => {
                const n = despachos.filter((d) => (d.estado_label || d.estado) === label).length;
                return (
                  <span key={label} className="rounded-tag bg-tareas/10 px-2 py-0.5 text-[11px] font-semibold text-tareas">
                    {label}{despachos.length > n ? ` (${n})` : ""}
                  </span>
                );
              })}
            </span>
          )}
        </div>
        {task.project_name && (
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-tinta-2">
            <Icon name="folder" size={13} /> {task.project_name}
          </div>
        )}
        {/* Cliente heredado del proyecto — informativo, se cambia desde el
            proyecto, no aquí (pedido del usuario 2026-08-07). Ocultable en
            Ajustes → Configuración para clientes que no usan este dato. */}
        {task.client_name && features?.tarea_cliente_mostrar !== false && (
          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-tinta-2">
            <Icon name="briefcase" size={13} /> {task.client_name}
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="mb-3 flex flex-wrap items-center gap-1">
        {task.collaborators?.length > 0 && (
          <span className="flex items-center gap-1 text-xs text-slate-500 mr-2">
            <div className="flex -space-x-1">{task.collaborators.map((c) => <Avatar key={c.id} name={c.full_name} src={c.avatar_url} size={20} />)}</div>
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {esFederado && (
            <span className="chip inline-flex items-center gap-1 !px-2 !py-0.5 text-[11px] font-semibold uppercase bg-indigo-100 text-indigo-700"
                  title="Tarea sincronizada desde Panamá — solo lectura en esta instancia">
              <Icon name="globe" size={12} /> Panamá · solo lectura
            </span>
          )}
          <FollowButton entityType="task" entityId={task.id} size="xs" />
          {task.progress_days > 0 && (
            <button onClick={() => { onClose?.(); navigate(`/tarea/${task.id}/seguimiento`); }}
              className="btn-ghost text-xs text-indigo-700" title="Ver línea de tiempo de avances (imprimible / enviable por email)">
              <Icon name="clock" size={14} /> Seguimiento
            </button>
          )}
          {canWrite && <button onClick={() => setSharing(true)} className="btn-ghost text-xs"><Icon name="share" size={14} /> Compartir</button>}
          {canWrite && <button onClick={duplicate} className="btn-ghost text-xs"><Icon name="copy" size={14} /> Duplicar</button>}
          {canWrite && <button onClick={() => onEdit?.(task)} className="btn-outline-primary text-xs font-semibold"><Icon name="edit" size={14} /> Editar</button>}
          {canDelete && <button onClick={removeTask} className="btn-ghost text-xs text-rose-600"><Icon name="trash" size={14} /> Eliminar</button>}
        </div>
      </div>

      <TabTrack>
        {TABS.map(([k, label, icon]) => (
          <TabButton key={k} active={tab === k} onClick={() => setTab(k)}>
            <Icon name={icon} size={14} />{label}
          </TabButton>
        ))}
      </TabTrack>

      <div className="min-h-[360px]">

      {/* BITÁCORA: evolución día a día (tareas tipo seguimiento / con avances) */}
      {tab === "bitacora" && (() => {
        if (progress === null) return <p className="py-8 text-center text-sm text-slate-400">Cargando bitácora…</p>;
        const dayKey = (p) => p.work_date || (p.created_at || "").slice(0, 10);
        const byDay = {};
        progress.forEach((p) => { const k = dayKey(p); if (k) (byDay[k] = byDay[k] || []).push(p); });
        const daysAsc = Object.keys(byDay).sort();
        const dayNumber = Object.fromEntries(daysAsc.map((d, i) => [d, i + 1]));
        const totalHours = progress.reduce((s, p) => s + (p.hours || 0), 0);
        const dayName = (d) => new Date(d + "T00:00:00").toLocaleDateString("es-VE", { weekday: "long", day: "2-digit", month: "short" });
        return (
          <div className="space-y-4">
            {/* Resumen */}
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg bg-indigo-50 px-3 py-2"><div className="text-[11px] font-bold uppercase text-indigo-500">Días con avance</div><div className="text-lg font-bold text-indigo-700">{daysAsc.length}</div></div>
              <div className="rounded-lg bg-emerald-50 px-3 py-2"><div className="text-[11px] font-bold uppercase text-emerald-600">Horas en bitácora</div><div className="text-lg font-bold text-emerald-700">{fmtHm(totalHours)}</div></div>
              <div className="rounded-lg bg-slate-100 px-3 py-2"><div className="text-[11px] font-bold uppercase text-slate-400">Periodo</div><div className="text-xs font-semibold text-slate-700">{daysAsc.length ? `${daysAsc[0].slice(5)} → ${daysAsc[daysAsc.length - 1].slice(5)}` : "—"}</div></div>
            </div>

            {/* Registrar avance de hoy */}
            {canProgress && (
              <div className="space-y-2 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">
                  {editEntryId ? "Editando avance" : "Registrar avance del día"}
                </div>
                <div className="flex gap-2">
                  <textarea className="input flex-1" rows={2} placeholder="¿Qué se hizo hoy? Describe el avance…"
                    value={newEntry.description} onChange={(e) => setNewEntry((p) => ({ ...p, description: e.target.value }))} />
                  <MicButton onText={(t) => setNewEntry((p) => ({ ...p, description: (p.description ? p.description + " " : "") + t }))} />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input type="date" className="input w-36" value={newEntry.work_date} onChange={(e) => setNewEntry((p) => ({ ...p, work_date: e.target.value }))} />
                  <div className="flex items-center gap-1" title="Tiempo dedicado en este avance (se suma al total de la tarea)">
                    <input type="number" min="0" placeholder="0" className="input h-9 w-14 text-center"
                      value={Math.floor(Number(newEntry.hours) || 0) || ""}
                      onChange={(e) => {
                        const h = Math.max(0, Math.floor(Number(e.target.value) || 0));
                        const m = Math.round(((Number(newEntry.hours) || 0) % 1) * 60);
                        setNewEntry((p) => ({ ...p, hours: String(h + m / 60) }));
                      }} aria-label="horas" />
                    <span className="text-xs text-slate-400">h</span>
                    <input type="number" min="0" max="59" placeholder="0" className="input h-9 w-14 text-center"
                      value={Math.round(((Number(newEntry.hours) || 0) % 1) * 60) || ""}
                      onChange={(e) => {
                        const m = Math.min(59, Math.max(0, Math.floor(Number(e.target.value) || 0)));
                        const h = Math.floor(Number(newEntry.hours) || 0);
                        setNewEntry((p) => ({ ...p, hours: String(h + m / 60) }));
                      }} aria-label="minutos" />
                    <span className="text-xs text-slate-400">m</span>
                  </div>
                  <select className="input flex-1 min-w-[8rem] text-sm" value={newEntry.participant_id || ""} onChange={(e) => setNewEntry((p) => ({ ...p, participant_id: e.target.value }))} title="¿Quién realizó el trabajo? (opcional)">
                    <option value="">Realizado por: yo</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </select>
                  {editEntryId && (
                    <button type="button" className="btn-ghost text-xs" onClick={cancelEditEntry}>Cancelar</button>
                  )}
                  <button className="btn-primary text-xs" disabled={savingEntry} onClick={addProgressEntry}>
                    <Icon name={editEntryId ? "check" : "plus"} size={14} /> {savingEntry ? "Guardando…" : editEntryId ? "Guardar" : "Registrar"}
                  </button>
                </div>
              </div>
            )}

            {/* Línea de tiempo día a día (más reciente arriba) */}
            <div className="max-h-96 space-y-3 overflow-y-auto pr-1">
              {daysAsc.length === 0 && <p className="rounded-lg border border-dashed border-slate-200 px-3 py-6 text-center text-xs text-slate-400">Aún no hay avances registrados. Registra el primero arriba.</p>}
              {[...daysAsc].reverse().map((d) => {
                const entries = byDay[d];
                const dayHours = entries.reduce((s, p) => s + (p.hours || 0), 0);
                return (
                  <div key={d} className="relative border-l-2 border-indigo-200 pl-4">
                    <span className="absolute -left-[7px] top-1 h-3 w-3 rounded-full border-2 border-white bg-indigo-500" />
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className="rounded-tag bg-indigo-100 px-2 py-0.5 text-[11px] font-bold text-indigo-700">Día {dayNumber[d]}</span>
                      <span className="text-xs font-semibold capitalize text-slate-700">{dayName(d)}</span>
                      {dayHours > 0 && <span className="text-[11px] font-bold text-indigo-500">{fmtHm(dayHours)}</span>}
                    </div>
                    <div className="space-y-1.5">
                      {entries.map((p) => (
                        <div key={p.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <div className="mb-0.5 flex items-center justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
                              {(p.participant || p.author) && <Avatar name={(p.participant || p.author).full_name} src={(p.participant || p.author).avatar_url} size={16} />}
                              <span className="font-medium text-slate-600">{(p.participant || p.author)?.full_name || "—"}</span>
                              {p.participant && p.author && p.participant.id !== p.author.id && (
                                <span className="text-[11px] text-slate-400">· registró {p.author.full_name.split(" ")[0]}</span>
                              )}
                              {p.hours > 0 && (
                                <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] font-bold text-indigo-700">{fmtHm(p.hours)}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {canProgress && (
                                <button onClick={() => { setUploadingForProgress(p.id); progressFileRef.current?.click(); }}
                                  className="text-slate-300 hover:text-indigo-500" title="Adjuntar archivo a este avance"><Icon name="paperclip" size={12} /></button>
                              )}
                              {p.author?.id === user?.id && canProgress && (
                                <>
                                  <button onClick={() => startEditEntry(p)} className="text-slate-300 hover:text-indigo-500" title="Editar"><Icon name="edit" size={12} /></button>
                                  <button onClick={() => deleteProgressEntry(p.id)} className="text-slate-300 hover:text-rose-500" title="Eliminar"><Icon name="trash" size={12} /></button>
                                </>
                              )}
                            </div>
                          </div>
                          {p.description && <div className="whitespace-pre-wrap text-sm text-slate-700">{p.description}</div>}
                          {p.attachments?.length > 0 && (
                            <div className="mt-1.5 space-y-1">
                              {p.attachments.map((a) => (
                                <div key={a.id} className="flex items-center gap-1.5 rounded border border-indigo-100 bg-indigo-50/50 px-2 py-1">
                                  <Icon name={fileIcon(a.content_type, a.filename)} size={13} className="text-indigo-400" />
                                  <span className="flex-1 truncate text-[11px] text-indigo-700">{a.filename}</span>
                                  {isPreviewable(a) && (
                                    <button onClick={() => setPreviewAtt(a)} className="text-[11px] text-indigo-500 hover:text-indigo-700" title="Vista previa"><Icon name="eye" size={12} /></button>
                                  )}
                                  <button onClick={() => download(a)} className="text-[11px] text-indigo-500 hover:text-indigo-700"><Icon name="download" size={12} /></button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="mt-1 flex items-center justify-end gap-1 text-[11px] text-slate-300"
                            title="Fecha y hora en que se guardó este registro (distinta a la fecha del avance)">
                            <Icon name="clock" size={9} /> Registrado {fmtStamp(p.created_at)}
                            {p.updated_at && p.updated_at !== p.created_at && <span>· editado {fmtStamp(p.updated_at)}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <input ref={progressFileRef} type="file" className="hidden" onChange={(e) => { uploadFile(e, uploadingForProgress); setUploadingForProgress(null); }} />
          </div>
        );
      })()}

      {/* CHAT estilo WhatsApp */}
      {tab === "chat" && (
        <div>
          <div className="h-80 space-y-2 overflow-y-auto rounded-lg p-3" style={{ background: "#efeae2" }}>
            {task.comments?.length === 0 && <p className="pt-12 text-center text-sm text-slate-500">Sin mensajes. Inicia la conversación.</p>}
            {task.comments?.map((c) => {
              const mine = c.author?.id === user?.id;
              return (
                <div key={c.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`group max-w-[78%] rounded-lg px-3 py-1.5 shadow-sm ${mine ? "rounded-br-none bg-[#d9fdd3]" : "rounded-bl-none bg-white"}`}>
                    {!mine && <div className="text-[11px] font-semibold text-brand-700">{c.author?.full_name || "Usuario"}</div>}
                    {cEditId === c.id ? (
                      <div className="flex flex-col gap-1">
                        <textarea rows={2} className="input w-60 resize-none text-sm" value={cEditText} onChange={(e) => setCEditText(e.target.value)} autoFocus />
                        <div className="flex justify-end gap-2 text-[11px]">
                          <button className="text-slate-400 hover:text-slate-600" onClick={() => setCEditId(null)}>Cancelar</button>
                          <button className="font-semibold text-brand-600" onClick={() => saveEditComment(c.id)}>Guardar</button>
                        </div>
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap break-words text-sm text-slate-800">{renderMessage(c.body)}</div>
                    )}
                    <div className="mt-0.5 flex items-center justify-end gap-1.5 text-[11px] text-slate-400">
                      {c.edited_at && <span className="italic">(editado)</span>}
                      <span>{fmtStamp(c.created_at)}</span>
                      {mine && cEditId !== c.id && (
                        <span className="hidden gap-1 group-hover:inline-flex">
                          <button className="hover:text-brand-600" onClick={() => { setCEditId(c.id); setCEditText(c.body); }} title="Editar"><Icon name="edit" size={11} /></button>
                          <button className="hover:text-rose-500" onClick={() => deleteComment(c.id)} title="Borrar"><Icon name="trash" size={11} /></button>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={chatEnd} />
          </div>
          {pendingFile && (
            <div className="mt-2 flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50/60 px-2 py-1.5 text-xs">
              <Icon name={isImageName(pendingFile.name) ? "eye" : "paperclip"} size={14} className="text-brand-500" />
              <span className="min-w-0 flex-1 truncate text-slate-700">{pendingFile.name}</span>
              <span className="text-slate-400">listo para enviar — escribe un mensaje o menciona</span>
              <button type="button" className="btn-ghost px-1 text-rose-500" onClick={() => setPendingFile(null)} title="Quitar"><Icon name="x" size={13} /></button>
            </div>
          )}
          <form onSubmit={addComment} className="relative mt-2 flex gap-2">
            {mentionMatches.length > 0 && (
              <div className="absolute bottom-12 left-0 z-10 w-64 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
                {mentionMatches.map((u) => (
                  <button key={u.id} type="button" onClick={() => pickMention(u)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50">
                    {u.team
                      ? <span className="flex h-5 w-5 items-center justify-center rounded bg-indigo-100 text-indigo-600"><Icon name="users" size={12} /></span>
                      : <Avatar name={u.name} src={u.avatar_url} size={20} />}
                    <span className="min-w-0 flex-1 truncate">{u.name}</span>
                    <span className="text-xs text-slate-400">@{u.handle}</span>
                  </button>
                ))}
              </div>
            )}
            <input className="input" placeholder="Escribe un mensaje…  (@ para mencionar)" value={comment}
              onChange={(e) => setComment(e.target.value)} />
            <input ref={chatFileRef} type="file" className="hidden" onChange={uploadChatFile} />
            <button type="button" className="btn-ghost px-2" title="Adjuntar archivo o foto" onClick={() => chatFileRef.current?.click()}><Icon name="paperclip" size={16} /></button>
            <MicButton onText={(t) => setComment((c) => (c ? c + " " : "") + t)} title="Dictar mensaje" />
            <VoiceNoteButton onText={(t) => setComment((c) => (c ? c + " " : "") + t)} title="Grabar y transcribir nota de voz" />
            <button className="btn-primary">Enviar</button>
          </form>
        </div>
      )}

      {/* INFO */}
      {tab === "info" && (
        <div className="space-y-4">
          {/* Creada por + tipo + fechas */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-slate-50 px-3 py-2 text-sm">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-bold uppercase text-slate-400">Creada por</span>
              {task.creator ? (
                <span className="flex items-center gap-1 font-medium text-slate-700"><Avatar name={task.creator.full_name} src={task.creator.avatar_url} size={20} />{task.creator.full_name}</span>
              ) : <span className="text-slate-400">—</span>}
            </div>
            {task.created_at && <span className="text-xs text-slate-400" title={roleAtLeast("admin") ? `Creada: ${fmtStamp(task.created_at)}` : undefined}>{roleAtLeast("admin") ? fmtStamp(task.created_at) : fmtStampDate(task.created_at)}</span>}
            {task.task_type && <span className="rounded-tag bg-brand-50 px-2 py-0.5 text-[11px] font-semibold capitalize text-brand-700">{({ inventario: "Equipos", solicitud: "Insumos" })[task.task_type] || task.task_type}</span>}
            {task.recurrence && <span className="rounded-tag bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">{({daily:"Diaria",weekly:"Semanal",biweekly:"Quincenal",monthly:"Mensual"})[task.recurrence] || task.recurrence}</span>}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div><div className="label">Inicio</div>{fmtDate(task.start_date)}</div>
            <div><div className="label">Compromiso</div>{fmtDate(task.due_date)}</div>
            {task.start_at && (
              <div className="col-span-2"><div className="label">Horario</div>
                <span className="inline-flex items-center gap-1"><Icon name="clock" size={14} className="text-brand-500" />{fmtDateTime(task.start_at)}{task.end_at ? ` – ${fmtDateTime(task.end_at)}` : ""}</span>
              </div>
            )}
            <div>
              <div className="label">Tiempo invertido</div>
              <HorasMin horas={task.hours_spent || 0}
                onSave={async (dec) => { await api.patch(`/tasks/${task.id}`, { hours_spent: dec }); load(); onChanged?.(); }} />
            </div>
          </div>

          <div>
            <div className="label">{isSupplyStage(task.stage) || ["solicitud", "inventario", "despacho"].includes(task.task_type) ? "Estado de suministro" : "Cambiar estado"}</div>
            {isSupplyStage(task.stage) || ["solicitud", "inventario", "despacho"].includes(task.task_type) ? (
              <div className="flex flex-wrap gap-2">
                {SUPPLY_ORDER.map((k) => {
                  const v = SUPPLY[k];
                  return <button key={k} onClick={() => changeSupply(k)} className={`chip ${task.stage === k ? v.color + " ring-2 ring-offset-1" : "bg-slate-100 text-slate-600"}`}>{v.label}</button>;
                })}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Object.entries(STATUS).map(([k, v]) => (
                  <button key={k} onClick={() => changeStatus(k)} className={`chip ${task.status === k ? v.color + " ring-2 ring-offset-1" : "bg-slate-100 text-slate-600"}`}>{v.label}</button>
                ))}
              </div>
            )}
          </div>

          {/* Descripción + conclusiones/cierre */}
          {(task.description || task.closure_note) && (
            <div className="space-y-2">
              {task.description && (
                <div>
                  <div className="label mb-1">Descripción</div>
                  {/* HTML seguro: admite <img>/<a> incrustados y convierte URLs
                      sueltas en enlaces clicables (RichText sanitiza). */}
                  <RichText text={task.description}
                    className="rounded-lg bg-slate-50 p-4 text-sm leading-relaxed text-slate-700" />
                </div>
              )}
              {task.closure_note && (
                <div className="whitespace-pre-wrap rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm leading-relaxed text-emerald-800">
                  <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-emerald-600">Cierre / Resultados</div>
                  {task.closure_note}
                </div>
              )}
            </div>
          )}

          {/* Campos personalizados SUELTOS (sin pestaña) — Fase 4.1 */}
          <CustomFieldsView scope="task" departmentId={task.department_id} taskType={task.task_type} value={task.custom_data} users={users} />

          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div>
              <div className="label">Asignada a</div>
              <div className="flex flex-wrap gap-2">
                {task.assignees?.map((a) => <span key={a.id} className="flex items-center gap-1 text-sm"><Avatar name={a.full_name} src={a.avatar_url} size={22} />{a.full_name}</span>)}
                {!task.assignees?.length && <span className="text-slate-400">Sin asignar</span>}
              </div>
            </div>
            {task.pmo?.length > 0 && (
              <div>
                <div className="label">PMO</div>
                <div className="flex flex-wrap gap-2">
                  {task.pmo.map((a) => <span key={a.id} className="flex items-center gap-1 text-sm"><Avatar name={a.full_name} src={a.avatar_url} size={22} />{a.full_name}</span>)}
                </div>
              </div>
            )}
          </div>

          {task.blocker_reason && <div className="rounded-lg bg-rose-50 p-2 text-sm text-rose-700"><b>Bloqueo:</b> {task.blocker_reason}</div>}
          {task.support_required && <div className="rounded-lg bg-amber-50 p-2 text-sm text-amber-700"><b>Apoyo:</b> {task.support_required}</div>}
          <div>
            <div className="label">Dependencias — esta tarea depende de</div>
            <div className="space-y-1">
              {task.dependencies?.map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded bg-slate-50 px-2 py-1 text-sm">
                  <span className="flex items-center gap-1.5 uppercase"><Icon name="link" size={14} className="text-slate-400" />{d.depends_on?.code ? d.depends_on.code + " · " : ""}{d.depends_on?.title}</span>
                  <button onClick={() => removeDep(d.id)} className="text-rose-400 hover:text-rose-600"><Icon name="x" size={13} /></button>
                </div>
              ))}
              {!task.dependencies?.length && <p className="text-xs text-slate-400">Sin dependencias.</p>}
            </div>
            <div className="relative mt-1" ref={depAnchorRef}>
              <input className="input" placeholder="Agregar dependencia: buscar tarea…" value={depQuery} onChange={(e) => setDepQuery(e.target.value)} />
              {depQuery && (
                <FloatingPopover anchorRef={depAnchorRef} maxHeight={160} className="rounded-lg border border-slate-200 bg-white shadow-lg">
                  {allTasks.filter((t) => t.id !== task.id && `${t.code || ""} ${t.title}`.toLowerCase().includes(depQuery.toLowerCase())).slice(0, 8).map((t) => (
                    <button key={t.id} onClick={() => addDep(t.id)} className="block w-full truncate px-3 py-1.5 text-left text-sm uppercase hover:bg-slate-50">{t.code ? t.code + " · " : ""}{t.title}</button>
                  ))}
                </FloatingPopover>
              )}
            </div>
          </div>

          {/* Puente a Compras: convertir estos ítems en una requisición. */}
          {["solicitud", "inventario"].includes(task.task_type) && (
            <ConvertirEnRequisicion taskId={task.id} onDone={onChanged} />
          )}

          {task.items?.length > 0 && task.task_type === "checklist_apertura" && (
            <div>
              <div className="flex items-center justify-between">
                <div className="label">Checklist de verificación ({task.items.filter((i) => i.done).length}/{task.items.length} listos)</div>
                <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => setConstanciaOpen(true)}>
                  Enviar constancia
                </button>
              </div>
              <div className="space-y-3">
                {[...new Set(task.items.map((i) => i.dia || "—"))].map((dia) => {
                  const filas = task.items.map((it, i) => ({ it, i })).filter(({ it }) => (it.dia || "—") === dia);
                  return (
                    <div key={dia} className="overflow-x-auto rounded-lg border border-slate-200">
                      <div className="border-b border-slate-100 bg-slate-50 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                        {dia} ({filas.filter(({ it }) => it.done).length}/{filas.length})
                      </div>
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                          <tr>
                            <th className="px-1 py-1 text-center">Orden</th>
                            <th className="px-2 py-1 text-center">Listo</th>
                            <th className="px-2 py-1 text-left">Módulo</th>
                            <th className="px-2 py-1 text-left">Ítem</th>
                            <th className="px-2 py-1 text-left">Responsable</th>
                            <th className="px-2 py-1 text-left">Detalle</th>
                            <th className="px-2 py-1 text-center">Verificado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filas.map(({ it, i }, pos) => (
                            <tr key={i} className={`border-t border-slate-100 ${it.done ? "bg-emerald-50/50" : ""}`}>
                              <td className="px-1 py-1">
                                <div className="flex flex-col items-center gap-0.5">
                                  <button type="button" className="text-slate-300 hover:text-slate-600 disabled:opacity-20"
                                    disabled={pos === 0} onClick={() => moverItem(dia, pos, -1)} title="Mover arriba">
                                    <Icon name="chevron-up" size={13} />
                                  </button>
                                  <button type="button" className="text-slate-300 hover:text-slate-600 disabled:opacity-20"
                                    disabled={pos === filas.length - 1} onClick={() => moverItem(dia, pos, 1)} title="Mover abajo">
                                    <Icon name="chevron-down" size={13} />
                                  </button>
                                </div>
                              </td>
                              <td className="px-2 py-1 text-center"><input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={Boolean(it.done)} onChange={() => toggleItem(i)} title="Marcar como verificado" /></td>
                              <td className="px-2 py-1 text-slate-500">{it.modulo || "—"}</td>
                              <td className={`px-2 py-1 font-medium ${it.done ? "text-slate-400 line-through" : ""}`}>{it.item}</td>
                              <td className="px-2 py-1">
                                <select
                                  className="input !h-7 !py-0 text-xs"
                                  value={it.responsable_tipo === "contratista" ? "contratista" : it.responsable_tipo === "usuario" && it.responsable_user_id ? String(it.responsable_user_id) : ""}
                                  onChange={(e) => setItemResponsable(i, e.target.value)}
                                >
                                  <option value="">{it.responsable || "— Sin asignar —"}</option>
                                  <option value="contratista">Contratista</option>
                                  {(users || []).filter((u) => u.department_id === task.department_id && u.is_active !== false).map((u) => (
                                    <option key={u.id} value={String(u.id)}>{u.full_name}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-1 text-xs text-slate-500">{it.detalle || ""}</td>
                              <td className="px-2 py-1 text-center">
                                {it.requiere_auditoria ? (
                                  <input type="checkbox" className="h-4 w-4 accent-amber-600"
                                    checked={Boolean(it.verificado)} disabled={!task.puede_auditar_checklist}
                                    onChange={() => toggleVerificado(i)}
                                    title={it.verificado ? `Verificado por ${it.verificado_por || "—"}`
                                      : task.puede_auditar_checklist ? "Marcar como verificado (auditoría)"
                                      : "Solo el auditor, el responsable del proyecto o un supervisor puede marcarlo"} />
                                ) : <span className="text-slate-300">—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
              <ConstanciaHistorial projectId={task.project_id} taskId={task.id} refreshKey={constanciaRefresh} />
            </div>
          )}

          {task.items?.length > 0 && task.task_type !== "checklist_apertura" && (() => {
            // "Insumos" (solicitud) es un pedido: se va marcando a medida que
            // llega, tiene sentido un checklist. "Envío de equipos"/despacho —
            // y cualquier otro tipo que caiga en el mismo cajón de items — es
            // un MANIFIESTO de algo que YA se despachó: no hay nada que
            // "marcar", todo el contenido ya salió. Pedido 2026-08-12: sin
            // checkbox, sin precio/subtotal (esta pantalla la puede abrir un
            // Country Manager, cero campos financieros — D-CM-05).
            const esSolicitud = task.task_type === "solicitud";
            const titulo = esSolicitud ? "Insumos" : task.task_type === "despacho" ? "Contenido del despacho" : "Equipos";
            return (
              <div>
                <div className="label">{titulo}{esSolicitud ? ` (${task.items.filter((i) => i.done).length}/${task.items.length} listos)` : ` (${task.items.length})`}</div>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                      <tr>
                        {esSolicitud && <th className="px-2 py-1 text-center">Listo</th>}
                        <th className="px-2 py-1 text-left">Categoría</th><th className="px-2 py-1 text-left">Material</th><th className="px-2 py-1 text-right">Cant.</th><th className="px-2 py-1 text-left">Unidad</th>
                        {esSolicitud && <><th className="px-2 py-1 text-right">Precio unit.</th><th className="px-2 py-1 text-right">Subtotal</th></>}
                        <th className="px-2 py-1 text-left">Estado</th><th className="px-2 py-1 text-left">Nota</th>
                      </tr>
                    </thead>
                    <tbody>
                      {task.items.map((it, i) => (
                        <tr key={i} className={`border-t border-slate-100 ${esSolicitud && it.done ? "bg-emerald-50/50" : ""}`}>
                          {esSolicitud && (
                            <td className="px-2 py-1 text-center"><input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={Boolean(it.done)} onChange={() => toggleItem(i)} title="Marcar como listo" /></td>
                          )}
                          <td className="px-2 py-1 text-slate-500">{it.category || "—"}</td>
                          <td className={`px-2 py-1 font-medium ${esSolicitud && it.done ? "text-slate-400 line-through" : ""}`}>{it.name}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{it.quantity}</td>
                          <td className="px-2 py-1 text-slate-500">{it.unit || ""}</td>
                          {esSolicitud && (
                            <>
                              <td className="px-2 py-1 text-right tabular-nums text-slate-500">
                                {it.precio_cents ? (it.precio_cents / 100).toLocaleString("es-PA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                              </td>
                              <td className="px-2 py-1 text-right font-medium tabular-nums text-slate-600">
                                {it.precio_cents ? ((it.precio_cents / 100) * (Number(it.quantity) || 0)).toLocaleString("es-PA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ""}
                              </td>
                            </>
                          )}
                          <td className="px-2 py-1">{it.estado && SUPPLY[it.estado] ? <span className={`chip ${SUPPLY[it.estado].color} !px-1.5 !py-0 text-[11px]`}>{SUPPLY[it.estado].label}</span> : <span className="text-slate-300">—</span>}</td>
                          <td className="px-2 py-1 text-xs text-slate-500">{it.nota || it.note || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      )}


      {/* ADJUNTOS */}
      {tab === "files" && (
        <div className="space-y-3">
          <div className="space-y-1">
            {task.attachments?.filter((a) => !a.superseded_at).map((a) => (
              <div key={a.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
                <Icon name={fileIcon(a.content_type, a.filename)} size={18} className="text-slate-400" />
                <div className="flex flex-1 flex-col truncate">
                  <span className="flex items-center gap-1.5 truncate text-sm">
                    {a.filename}
                    {a.version > 1 && <span className="chip bg-amber-50 text-amber-700 !px-1.5 !py-0 text-[11px] font-bold" title="Versión">v{a.version}</span>}
                  </span>
                  <span className="flex items-center gap-2 text-[11px] text-slate-400">
                    {a.uploaded_by && <span>{a.uploaded_by.full_name}</span>}
                    {a.view_count > 0 && <span className="inline-flex items-center gap-0.5" title="vistas"><Icon name="eye" size={9} />{a.view_count}</span>}
                    {a.download_count > 0 && <span className="inline-flex items-center gap-0.5" title="descargas"><Icon name="download" size={9} />{a.download_count}</span>}
                    {a.progress_id && <span className="text-indigo-500"><Icon name="calendar" size={10} className="inline" /> Bitácora {a.progress_work_date ? a.progress_work_date.slice(5) : ""}</span>}
                  </span>
                </div>
                {isPreviewable(a) && (
                  <button onClick={() => setPreviewAtt(a)} className="btn-ghost px-2 py-1 text-xs" title="Vista previa"><Icon name="eye" size={13} /> Ver</button>
                )}
                <button onClick={() => download(a)} className="btn-ghost px-2 py-1 text-xs"><Icon name="download" size={13} /> Descargar</button>
                <button onClick={() => delAtt(a)} className="text-xs text-slate-300 hover:text-rose-500"><Icon name="trash" size={14} /></button>
              </div>
            ))}
            {!task.attachments?.length && <p className="py-4 text-center text-sm text-slate-400">Sin adjuntos. Sube imágenes, PDF, DOC, etc.</p>}
          </div>
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => uploadFile(e)} />
          <button onClick={() => fileRef.current?.click()} className="btn-ghost w-full justify-center border border-dashed border-slate-300"><Icon name="paperclip" size={16} /> Subir archivo</button>
        </div>
      )}

      {/* (El tab "Tiempos" se eliminó a pedido del usuario: confundía y la
          info de duración por estado no se estaba usando.) */}

      {/* ACTIVIDAD */}
      {tab === "activity" && (
        <div className="max-h-80 space-y-2 overflow-y-auto text-sm">
          {task.activity?.map((a) => (
            <div key={a.id} className="flex gap-2 border-b border-slate-100 pb-2">
              <span className="text-slate-400">{fmtStamp(a.created_at)}</span>
              <span><b>{a.user?.full_name || "Sistema"}</b> {a.action}{a.field ? ` · ${a.field}` : ""}{a.new_value ? <span className="text-slate-500"> → {a.new_value}</span> : ""}</span>
            </div>
          ))}
          {!task.activity?.length && <p className="text-slate-400">Sin actividad.</p>}
        </div>
      )}

      {tab === "despachos" && (
        <div className="max-h-96 space-y-2 overflow-y-auto text-sm">
          {despachos === null ? (
            <p className="text-slate-400">Cargando…</p>
          ) : despachos.length === 0 ? (
            <p className="text-slate-400">Esta tarea no tiene cajas de envío asociadas.</p>
          ) : (
            despachos.map((r) => (
              <div key={r.box_id} className="rounded-lg border border-slate-200 px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-700">Caja #{r.box_number}</span>
                  {r.destino && <span className="text-xs text-slate-400">→ {r.destino}</span>}
                  <span className="chip ml-auto text-[11px] bg-slate-100 text-slate-600">{r.estado_label || r.estado}</span>
                </div>
                {r.contenedor_numero && (
                  <div className="mt-0.5 text-xs text-slate-500">
                    Contenedor {r.contenedor_numero}{r.contenedor_operador ? ` · ${r.contenedor_operador}` : ""}
                    {r.fecha_salida ? ` · salió ${r.fecha_salida}` : ""}
                  </div>
                )}
                {(r.items || []).length === 0 ? (
                  <p className="mt-1 text-xs text-slate-400">Sin contenido registrado</p>
                ) : (
                  <table className="mt-1 w-full">
                    <tbody>
                      {r.items.map((it, i) => (
                        <tr key={i} className="border-t border-slate-50 first:border-t-0">
                          <td className="py-0.5 pr-2 text-xs text-slate-600">{it.name}</td>
                          <td className="py-0.5 text-right text-[11px] tabular-nums text-slate-400 whitespace-nowrap">
                            {it.quantity ?? ""}{it.unit ? ` ${it.unit}` : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))
          )}
          {despachos && despachos.length > 0 && (
            <button className="btn-ghost text-xs" onClick={() => navigate("/compras/almacen")}>
              <Icon name="package" size={13} /> Ver en Almacén
            </button>
          )}
        </div>
      )}

      {/* C2 — pestaña personalizada activa: sus campos (solo lectura). */}
      {tab.startsWith("ct:") && (
        <div className="space-y-3">
          {canWrite && (
            <div className="flex justify-end">
              <button onClick={() => onEdit?.(task)} className="btn-ghost text-xs text-brand-700"><Icon name="edit" size={13} /> Completar datos</button>
            </div>
          )}
          <CustomFieldsView scope="task" departmentId={task.department_id} taskType={task.task_type}
            value={task.custom_data} users={users} tabId={Number(tab.slice(3))} />
        </div>
      )}

      </div>

      {/* Modal compartir */}
      {sharing && (
        <ShareDialog task={task} users={users} onClose={() => setSharing(false)} onSave={setCollaborators} />
      )}
      </div>
      {previewAtt && (
        <AttachmentPreview
          attachment={previewAtt}
          downloadUrl={`/tasks/${task.id}/attachments/${previewAtt.id}/download`}
          onClose={() => setPreviewAtt(null)}
        />
      )}
      <ConstanciaModal projectId={task.project_id} task={task} open={constanciaOpen}
        onClose={() => setConstanciaOpen(false)}
        onSent={() => { onChanged?.(); setConstanciaRefresh((n) => n + 1); }} />
    </Modal>
  );
}

function ShareDialog({ task, users, onClose, onSave }) {
  const [sel, setSel] = useState(task.collaborators?.map((c) => c.id) || []);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-1 text-base font-semibold">Compartir tarea</h3>
        <p className="mb-3 text-xs text-slate-500">Los colaboradores pueden ver, chatear y agregar subtareas.</p>
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {users.map((u) => (
            <label key={u.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50">
              <input type="checkbox" className="h-4 w-4 accent-brand-600" checked={sel.includes(u.id)} onChange={() => toggle(u.id)} />
              <Avatar name={u.full_name} src={u.avatar_url} size={24} />
              <span className="text-sm">{u.full_name}</span>
            </label>
          ))}
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" onClick={() => onSave(sel)}>Guardar</button>
        </div>
      </div>
    </div>
  );
}
